import { AlertTriangle, ChevronDown, MessageCircle, RotateCcw, Send } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import {
  type IntakeMessage,
  type IntakeResult,
  SNOOTS_HQ,
  SNOOTS_US,
  VETERIS,
} from '@snootsvet/shared/portal'

import { PageContainer } from '@/customer/components/page-container'
import type { PetScopeContext } from '@/customer/components/pet-scope'
import { Alert, AlertDescription, AlertTitle } from '@/customer/components/ui/alert'
import { Badge } from '@/customer/components/ui/badge'
import { Button } from '@/customer/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/customer/components/ui/collapsible'
import { Textarea } from '@/customer/components/ui/textarea'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { usePostIntakeChat } from '@/customer/hooks/use-post-intake-chat'
import { useRegion } from '@/customer/hooks/use-region'
import { cn } from '@/customer/lib/utils'
import { routes } from '@/customer/routes'

/**
 * Pre-visit intake chat. Internal prototype: reached by URL only, and the
 * backend answers 503 unless the feature is switched on. The transcript lives
 * here in state and is sent whole on every turn; nothing is persisted. The
 * assistant takes the history for a booked appointment and ends with a
 * summary for the vet.
 */
export function IntakePage() {
  const { pet } = useOutletContext<PetScopeContext>()
  useDocumentTitle(`Chat about ${pet.name}`)

  const chat = usePostIntakeChat(pet.id)
  const [messages, setMessages] = useState<IntakeMessage[]>([])
  const [result, setResult] = useState<IntakeResult | null>(null)
  const [draft, setDraft] = useState('')
  const [unavailable, setUnavailable] = useState(false)
  /** The transcript currently being sent; null between turns. Doubles as the pending flag. */
  const [inFlight, setInFlight] = useState<IntakeMessage[] | null>(null)
  const [failed, setFailed] = useState(false)
  const pending = inFlight !== null

  // Turns are driven off mutateAsync's promise and local state rather than
  // mutate() callbacks and chat.isPending: the opening turn fires from a mount
  // effect, which under StrictMode happens before useMutation's subscription
  // is rebuilt, and TanStack drops a detached observer's callbacks and never
  // clears its isPending (see use-post-intake-chat.test.ts).
  const runTurn = async (transcript: IntakeMessage[], onFailure?: () => void) => {
    setInFlight(transcript)
    setFailed(false)
    try {
      const response = await chat.mutateAsync({ messages: transcript })
      if (response.kind === 'reply') {
        setMessages([...transcript, { role: 'assistant', content: response.message }])
      } else {
        setMessages([...transcript, { role: 'assistant', content: response.result.closingMessage }])
        setResult(response.result)
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'IntakeUnavailableError') {
        setUnavailable(true)
      } else {
        setFailed(true)
        onFailure?.()
      }
    } finally {
      setInFlight(null)
    }
  }

  const start = () => {
    setMessages([])
    setResult(null)
    setDraft('')
    void runTurn([])
  }

  // Ref-based StrictMode guard: the opening turn must only fire once even
  // when the effect runs twice on mount in dev.
  const openedRef = useRef(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: the opening turn fires once per mount
  useEffect(() => {
    if (openedRef.current) return
    openedRef.current = true
    start()
  }, [])

  const handleSend = () => {
    const content = draft.trim()
    if (!content || pending || result) return
    setDraft('')
    // The owner's message only joins the transcript once the turn succeeds; on failure it goes back in the box.
    void runTurn([...messages, { role: 'user', content }], () => setDraft(content))
  }

  // While a turn is in flight, show the transcript we sent, which already includes the owner's new message.
  const visibleMessages = inFlight ?? messages

  return (
    <PageContainer
      backLink={{ to: routes.account.index, label: 'Back' }}
      headline={[
        { text: 'A few questions', color: 'yellow' },
        { text: `before ${pet.name}'s visit`, color: 'blue' },
      ]}
    >
      <div className="flex flex-col gap-6">
        {visibleMessages.length > 0 || pending ? (
          <IntakeTranscript messages={visibleMessages} typing={pending} />
        ) : null}
        {unavailable ? (
          <IntakeUnavailable />
        ) : result ? (
          <IntakeResultCard result={result} onStartOver={start} />
        ) : (
          <IntakeComposer
            value={draft}
            onChange={setDraft}
            onSend={handleSend}
            disabled={pending}
            error={failed ? "We couldn't send that. Please try again." : null}
          />
        )}
      </div>
    </PageContainer>
  )
}

function IntakeTranscript({ messages, typing }: { messages: IntakeMessage[]; typing: boolean }) {
  const containerRef = useRef<HTMLDivElement>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll whenever the transcript grows or the indicator toggles
  useEffect(() => {
    const el = containerRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages.length, typing])

  return (
    <div
      ref={containerRef}
      className="flex max-h-[60vh] min-h-64 flex-col gap-3 overflow-y-auto rounded-lg border-2 border-border bg-background p-4"
      aria-live="polite"
    >
      {messages.map((message, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: the transcript is append-only, so the index is stable
        <MessageBubble key={index} message={message} />
      ))}
      {typing ? <TypingIndicator /> : null}
    </div>
  )
}

function MessageBubble({ message }: { message: IntakeMessage }) {
  const own = message.role === 'user'
  return (
    <div className={cn('flex', own ? 'justify-end' : 'justify-start')}>
      <p
        className={cn(
          'max-w-[85%] whitespace-pre-wrap rounded-lg px-4 py-3 text-body-md',
          own ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
        )}
      >
        {message.content}
      </p>
    </div>
  )
}

function TypingIndicator() {
  return (
    <div className="flex justify-start" role="status" aria-label="The assistant is typing">
      <div className="flex gap-1 rounded-lg bg-muted px-4 py-4">
        {[0, 150, 300].map((delay) => (
          <span
            key={delay}
            className="size-2 animate-bounce rounded-full bg-muted-foreground"
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </div>
    </div>
  )
}

function IntakeComposer({
  value,
  onChange,
  onSend,
  disabled,
  error,
}: {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  disabled: boolean
  error: string | null
}) {
  return (
    <div className="flex flex-col gap-2">
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            onSend()
          }
        }}
        placeholder="Type your reply…"
        aria-label="Your message"
        disabled={disabled}
        maxLength={2000}
        className="min-h-12"
      />
      {error ? <p className="text-body-sm text-destructive">{error}</p> : null}
      <Button
        type="button"
        onClick={onSend}
        disabled={disabled || !value.trim()}
        className="self-end"
      >
        <Send data-icon="inline-start" />
        Send
      </Button>
    </div>
  )
}

function IntakeUnavailable() {
  return (
    <Alert variant="info">
      <MessageCircle />
      <AlertTitle>The assistant isn't available right now</AlertTitle>
      <AlertDescription>
        This is an internal preview. Your appointment is unaffected.
      </AlertDescription>
    </Alert>
  )
}

const URGENCY_COPY: Record<
  IntakeResult['urgency'],
  { label: string; variant: 'destructive' | 'warning' | 'info' }
> = {
  urgent: { label: 'Urgent', variant: 'destructive' },
  same_day: { label: 'Same day', variant: 'warning' },
  routine: { label: 'Routine', variant: 'info' },
}

/**
 * What the owner sees once the assistant has finished: an urgency banner when
 * it matters, the summary the vet would receive, and a way to start again.
 */
function IntakeResultCard({
  result,
  onStartOver,
}: {
  result: IntakeResult
  onStartOver: () => void
}) {
  return (
    <div className="flex flex-col gap-4">
      <UrgencyBanner result={result} />
      <VetSummary result={result} />
      <Button type="button" variant="outline" className="w-full" onClick={onStartOver}>
        <RotateCcw data-icon="inline-start" />
        Start over
      </Button>
    </div>
  )
}

/** Only SAME DAY and URGENT get a banner; routine needs nothing beyond the closing message. */
function UrgencyBanner({ result }: { result: IntakeResult }) {
  if (result.urgency === 'urgent') {
    return (
      <Alert variant="destructive">
        <AlertTriangle />
        <AlertTitle>This shouldn't wait for the appointment</AlertTitle>
        <AlertDescription>
          <p>{result.urgencyReason}</p>
          <EmergencyContacts />
        </AlertDescription>
      </Alert>
    )
  }
  if (result.urgency === 'same_day') {
    return (
      <Alert variant="warning">
        <AlertTriangle />
        <AlertTitle>The clinic may bring this appointment forward</AlertTitle>
        <AlertDescription>{result.urgencyReason}</AlertDescription>
      </Alert>
    )
  }
  return null
}

/** The same numbers the assistant relays in chat, as tappable links. */
function EmergencyContacts() {
  const region = useRegion()

  if (region.id === 'uk') {
    return (
      <p className="mt-2 font-bold">
        Call Snoots on{' '}
        <a href={telHref(SNOOTS_HQ.phone)} className="underline">
          {SNOOTS_HQ.phone}
        </a>{' '}
        or{' '}
        <a
          href={SNOOTS_HQ.whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          WhatsApp
        </a>
        . Out of hours: {VETERIS.name} on{' '}
        <a href={telHref(VETERIS.phone)} className="underline">
          {VETERIS.phone}
        </a>
        .
      </p>
    )
  }

  return (
    <p className="mt-2 font-bold">
      Call Snoots Clifton on{' '}
      <a href={telHref(SNOOTS_US.phone)} className="underline">
        {SNOOTS_US.phone}
      </a>{' '}
      or text {SNOOTS_US.textPhone}.
    </p>
  )
}

/** Strips display formatting so a printed number works as a tel: link. */
function telHref(phone: string): string {
  return `tel:${phone.replace(/[^+\d]/g, '')}`
}

/**
 * Prototype-only: the summary the vet would receive, shown open so the team
 * can judge it. Lands on the appointment when this is productionised.
 */
function VetSummary({ result }: { result: IntakeResult }) {
  const [open, setOpen] = useState(true)
  const urgency = URGENCY_COPY[result.urgency]

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-lg border-2 border-dashed border-border p-4"
    >
      <CollapsibleTrigger className="flex w-full items-center justify-between text-left text-label-md">
        Summary for the vet (prototype preview)
        <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-4 flex flex-col gap-4 text-body-sm">
        {result.incompleteNote ? (
          <p className="font-bold uppercase">Incomplete: {result.incompleteNote}</p>
        ) : null}
        <SummaryField label="Patient" text={result.patient} />
        <div>
          <p className="font-bold">Urgency</p>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={urgency.variant}>{urgency.label}</Badge>
            {result.urgencyReason ? <span>{result.urgencyReason}</span> : null}
          </div>
        </div>
        <SummaryField label="Presenting complaint" text={result.presentingComplaint} />
        <SummaryField label="Status now" text={result.statusNow} />
        <SummaryField label="History of present illness" text={result.history} />
        <SummaryField label="Systems review" text={result.systemsReview} />
        <SummaryField label="Relevant background" text={result.relevantBackground} />
        <SummaryField label="Owner's concern and expectations" text={result.ownerConcern} />
        <SummaryField label="Attachments and home checks" text={result.homeChecks} />
        <SummaryField label="Practicalities" text={result.practicalities} />
        <div>
          <p className="font-bold">Open questions for the exam</p>
          <ul className="list-disc pl-5">
            {result.openQuestions.map((question) => (
              <li key={question}>{question}</li>
            ))}
          </ul>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

/** Renders one labelled block of summary text. */
function SummaryField({ label, text }: { label: string; text: string }) {
  return (
    <div>
      <p className="font-bold">{label}</p>
      <p className="whitespace-pre-wrap">{text}</p>
    </div>
  )
}
